import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { dnoAPI } from '../lib/api';
import { GenMeterSelect } from './GenMeterSelect';
import { Loader2, Wand2, CheckCircle2, Upload, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const FIELD_LABELS = {
  inverter_output_kw: 'Inverter output (kW)',
  mpan: 'MPAN',
  project_name: 'Project name',
  no_of_phases: 'Number of phases',
  cutout_size: 'Cut-out size (A)',
  panel_manufacturer: 'Panel manufacturer',
  panel_model: 'Panel model',
  panel_quantity: 'Panel quantity',
};

/**
 * "Auto-generate SLD" — builds a personalised Single Line Diagram, saves it to
 * the customer's Documents and returns a file_url to attach to the application.
 */
export const SldGenerateDialog = ({ customer, phaseCode, cutoutRating, onGenerated, triggerClassName, triggerLabel }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState('');
  const [computed, setComputed] = useState(null);
  const [form, setForm] = useState({});
  const [notes, setNotes] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  const [version, setVersion] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isAlternative, setIsAlternative] = useState(false);
  const fileInputRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const initForm = () => {
    const hasPanels = !!(customer?.panel_quantity || customer?.panel_manufacturer);
    const hasBattery = !!(customer?.battery_quantity || customer?.battery_manufacturer);
    // String auto-detect from panel quantity (overridable): 1 string up to 12
    // panels, otherwise split as evenly as possible.
    const pq = parseInt(customer?.panel_quantity, 10);
    let ns = '', pps = '';
    if (pq && pq > 0) {
      const strings = pq <= 12 ? 1 : Math.ceil(pq / 12);
      ns = String(strings);
      pps = String(Math.round(pq / strings));
    }
    // Prefer a real string layout captured from the design, else the heuristic.
    if (customer?.num_strings) ns = String(customer.num_strings);
    if (customer?.panels_per_string) pps = String(customer.panels_per_string);
    setForm({
      inverter_manufacturer: customer?.inverter_manufacturer || '',
      inverter_model: customer?.inverter_model || '',
      inverter_output_kw: '',
      inverter_quantity: customer?.inverter_quantity || 1,
      gen_meter_model: customer?.gen_meter_model || '',
      dc_cable_size: '6',
      no_of_phases: phaseCode === 3 || phaseCode === '3' ? 'Three' : 'Single',
      cutout_size: cutoutRating ? String(cutoutRating) : '',
      mpan: customer?.mpan || customer?.mpan_number || '',
      project_name: customer?.project_name || customer?.job_name || '',
      has_panels: hasPanels,
      has_battery: hasBattery,
      has_home_backup: false,
      home_backup_model: 'Fox ESS Home Backup',
      has_ev_charger: false,
      ev_charger_model: '',
      num_strings: ns,
      panels_per_string: pps,
      panel_manufacturer: customer?.panel_manufacturer || '',
      panel_model: customer?.panel_model || '',
      panel_quantity: customer?.panel_quantity || '',
    });
    setPreview('');
    setComputed(null);
    setNotes('');
    setVersion('');
    setIsAlternative(false);
  };

  const openDialog = () => {
    if (!customer?.id) { toast.error('Select a customer first'); return; }
    initForm();
    setOpen(true);
  };

  const suggestNotes = async () => {
    setNotesLoading(true);
    try {
      const payload = { ...form };
      if (payload.inverter_output_kw !== '') payload.inverter_output_kw = Number(payload.inverter_output_kw);
      payload.dc_cable_size = Number(payload.dc_cable_size || 6);
      const { data } = await dnoAPI.suggestSldNotes(customer.id, payload);
      setNotes((data.notes || []).join('\n'));
      toast.success(data.ai ? 'AI notes suggested — edit as needed' : 'Standard notes loaded — edit as needed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not suggest notes');
    } finally {
      setNotesLoading(false);
    }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.inverter_output_kw !== '') payload.inverter_output_kw = Number(payload.inverter_output_kw);
      payload.dc_cable_size = Number(payload.dc_cable_size || 6);
      if (notes.trim()) payload.notes = notes;
      const { data } = await dnoAPI.generateSld(customer.id, payload);
      if (data.needs_input) {
        toast.error('Please complete the highlighted fields to generate the SLD');
        setForm((f) => ({ ...f, _missing: data.missing || [] }));
        return;
      }
      setPreview(data.preview_image || '');
      setComputed(data.computed || null);
      setVersion(data.version || '');
      setIsAlternative(false);
      if (Array.isArray(data.notes) && data.notes.length && !notes.trim()) {
        setNotes(data.notes.join('\n'));
      }
      toast.success('SLD generated, saved to customer files and attached');
      if (onGenerated) onGenerated(data.sld_file_url, data.sld_filename);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate SLD');
    } finally {
      setLoading(false);
    }
  };

  const onPickAlternative = () => fileInputRef.current?.click();

  const uploadAlternative = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await dnoAPI.uploadSld(customer.id, file);
      setPreview(data.preview_image || '');
      setComputed(null);
      setVersion('');
      setIsAlternative(true);
      toast.success('Alternative SLD uploaded, saved to files and attached');
      if (onGenerated) onGenerated(data.sld_file_url, data.sld_filename);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not upload SLD');
    } finally {
      setUploading(false);
    }
  };

  const missing = form._missing || [];
  const isMissing = (k) => missing.includes(k);

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className={triggerClassName || "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded cursor-pointer border border-blue-300 bg-white text-blue-700 hover:bg-blue-50 transition-colors"}
        data-testid="sld-generate-btn"
      >
        <Wand2 className="w-3 h-3" /> {triggerLabel || 'Auto-generate SLD'}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="sld-generate-dialog">
          <DialogHeader>
            <DialogTitle>Auto-generate Single Line Diagram</DialogTitle>
            <DialogDescription>
              Builds a personalised SLD from this customer's equipment, saves it to their Documents and attaches it to the application.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Inverter manufacturer</Label>
              <Input value={form.inverter_manufacturer ?? ''} onChange={(e) => set('inverter_manufacturer', e.target.value)}
                className={`h-9 ${isMissing('inverter_manufacturer') ? 'border-rose-400' : ''}`}
                placeholder="e.g. Fox ESS" data-testid="sld-inverter-mfr" />
            </div>
            <div>
              <Label className="text-xs">Inverter model</Label>
              <Input value={form.inverter_model ?? ''} onChange={(e) => set('inverter_model', e.target.value)}
                className={`h-9 ${isMissing('inverter_model') ? 'border-rose-400' : ''}`}
                placeholder="e.g. H1-6.0" data-testid="sld-inverter-model" />
            </div>
            <div>
              <Label className="text-xs">Inverter output (kW)</Label>
              <Input type="number" step="0.1" value={form.inverter_output_kw ?? ''}
                onChange={(e) => set('inverter_output_kw', e.target.value)}
                className={`h-9 ${isMissing('inverter_output_kw') ? 'border-rose-400' : ''}`}
                placeholder="e.g. 6.0" data-testid="sld-inverter-output" />
              <p className="text-[11px] text-slate-500 mt-0.5">Saved back to the product catalogue for next time.</p>
            </div>
            <div>
              <Label className="text-xs">Number of inverters</Label>
              <Input type="number" min="1" step="1" value={form.inverter_quantity ?? 1}
                onChange={(e) => set('inverter_quantity', e.target.value)}
                className="h-9" placeholder="1" data-testid="sld-inverter-qty" />
            </div>
            <div>
              <Label className="text-xs">Generation meter</Label>
              <GenMeterSelect value={form.gen_meter_model ?? ''} onChange={(v) => set('gen_meter_model', v)}
                inverterManufacturer={form.inverter_manufacturer} threePhase={String(form.no_of_phases).toLowerCase().startsWith('three')}
                className="h-9" testId="sld-gen-meter-model" />
              <p className="text-[11px] text-slate-500 mt-0.5">Auto: Solis / SunSynk → Eastron, otherwise Emlite ECA2A-NZ. Saved to the customer.</p>
            </div>
            <div>
              <Label className="text-xs">DC cable size (mm²)</Label>
              <Select value={String(form.dc_cable_size || '6')} onValueChange={(v) => set('dc_cable_size', v)}>
                <SelectTrigger className="h-9" data-testid="sld-dc-cable"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['4', '6', '10'].map((o) => <SelectItem key={o} value={o}>{o} mm²</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Number of phases</Label>
              <Select value={form.no_of_phases || 'Single'} onValueChange={(v) => set('no_of_phases', v)}>
                <SelectTrigger className={`h-9 ${isMissing('no_of_phases') ? 'border-rose-400' : ''}`} data-testid="sld-phases"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Single">Single</SelectItem>
                  <SelectItem value="Three">Three</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cut-out size (A)</Label>
              <Input value={form.cutout_size ?? ''} onChange={(e) => set('cutout_size', e.target.value)}
                className={`h-9 ${isMissing('cutout_size') ? 'border-rose-400' : ''}`} placeholder="e.g. 80"
                data-testid="sld-cutout" />
            </div>
            <div>
              <Label className="text-xs">MPAN</Label>
              <Input value={form.mpan ?? ''} onChange={(e) => set('mpan', e.target.value)}
                className={`h-9 ${isMissing('mpan') ? 'border-rose-400' : ''}`} data-testid="sld-mpan" />
            </div>
            <div>
              <Label className="text-xs">Project name</Label>
              <Input value={form.project_name ?? ''} onChange={(e) => set('project_name', e.target.value)}
                className={`h-9 ${isMissing('project_name') ? 'border-rose-400' : ''}`} data-testid="sld-project" />
            </div>
            {form.has_panels && (
              <>
                <div>
                  <Label className="text-xs">Number of PV strings</Label>
                  <Input value={form.num_strings ?? ''} onChange={(e) => set('num_strings', e.target.value)}
                    className="h-9" placeholder="e.g. 2" data-testid="sld-num-strings" />
                </div>
                <div>
                  <Label className="text-xs">Panels per string</Label>
                  <Input value={form.panels_per_string ?? ''} onChange={(e) => set('panels_per_string', e.target.value)}
                    className="h-9" placeholder="e.g. 6" data-testid="sld-panels-per-string" />
                </div>
              </>
            )}
            {isMissing('panel_manufacturer') || isMissing('panel_model') || isMissing('panel_quantity') ? (
              <>
                <div><Label className="text-xs">Panel manufacturer</Label>
                  <Input value={form.panel_manufacturer ?? ''} onChange={(e) => set('panel_manufacturer', e.target.value)} className="h-9" /></div>
                <div><Label className="text-xs">Panel model</Label>
                  <Input value={form.panel_model ?? ''} onChange={(e) => set('panel_model', e.target.value)} className="h-9" /></div>
                <div><Label className="text-xs">Panel quantity</Label>
                  <Input value={form.panel_quantity ?? ''} onChange={(e) => set('panel_quantity', e.target.value)} className="h-9" /></div>
              </>
            ) : null}
          </div>

          <div className="border-t pt-3 mt-1 space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={!!form.has_home_backup} onCheckedChange={(v) => set('has_home_backup', !!v)}
                data-testid="sld-home-backup" />
              This install has a Fox ESS Home Backup (show on the drawing)
            </label>
            {form.has_home_backup && (
              <Input value={form.home_backup_model ?? ''} onChange={(e) => set('home_backup_model', e.target.value)}
                className="h-9" placeholder="Home backup model" data-testid="sld-home-backup-model" />
            )}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={!!form.has_ev_charger} onCheckedChange={(v) => set('has_ev_charger', !!v)}
                data-testid="sld-ev-charger" />
              This install has an EV charger (show on the drawing)
            </label>
            {form.has_ev_charger && (
              <Input value={form.ev_charger_model ?? ''} onChange={(e) => set('ev_charger_model', e.target.value)}
                className="h-9" placeholder="e.g. Zappi 7.4kW" data-testid="sld-ev-charger-model" />
            )}
          </div>

          <div className="border-t pt-3 mt-1 space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Drawing notes (one per line — shown on the SLD)</Label>
              <button type="button" onClick={suggestNotes} disabled={notesLoading}
                className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 disabled:opacity-50"
                data-testid="sld-suggest-notes-btn">
                {notesLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Suggest with AI
              </button>
            </div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5}
              placeholder="Click 'Suggest with AI' to draft compliance notes, then tweak them here. Leave blank to use the standard notes."
              className="text-xs" data-testid="sld-notes" />
          </div>

          {computed && (
            <div className="rounded bg-slate-50 border border-slate-200 p-2.5 text-xs text-slate-700 flex flex-wrap gap-x-4 gap-y-1">
              <span><b>Isolator:</b> {computed.isolator_size}A</span>
              <span><b>Max AC current:</b> {computed.max_ac_current}A</span>
              <span><b>AC cable:</b> {computed.ac_cable_size}mm²</span>
              <span><b>DC cable:</b> {computed.dc_cable_size}mm²</span>
              {version && <span data-testid="sld-version"><b>Version:</b> {version}</span>}
            </div>
          )}

          {preview && (
            <div className="border rounded overflow-hidden" data-testid="sld-preview">
              <div className="bg-emerald-50 text-emerald-800 text-xs px-3 py-1.5 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {isAlternative
                  ? 'Alternative SLD uploaded — saved to files and attached to this application'
                  : `Preview${version ? ` (v${version})` : ''} — saved to files and attached to this application`}
              </div>
              <img src={preview} alt="SLD preview" className="w-full" />
              <div className="bg-slate-50 text-slate-600 text-[11px] px-3 py-2 border-t flex items-center justify-between gap-2">
                <span>Not happy with this drawing? You can upload your own SLD instead.</span>
                <button type="button" onClick={onPickAlternative} disabled={uploading}
                  className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 whitespace-nowrap disabled:opacity-50"
                  data-testid="sld-upload-alternative-review">
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  Skip &amp; upload alternative
                </button>
              </div>
            </div>
          )}

          <input ref={fileInputRef} type="file" accept=".pdf,image/*" className="hidden"
            onChange={uploadAlternative} data-testid="sld-upload-input" />

          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2">
            <Button type="button" variant="outline" onClick={onPickAlternative} disabled={uploading}
              className="sm:mr-auto" data-testid="sld-upload-alternative-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              Skip &amp; upload alternative SLD
            </Button>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
              <Button onClick={generate} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="sld-generate-confirm">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wand2 className="w-4 h-4 mr-1" />}
                {preview && !isAlternative ? 'Regenerate' : 'Generate SLD'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SldGenerateDialog;
